/**
 * Gen 6 — ручная TS-схема брифа «Давайте знакомиться!».
 *
 * Источник правды: `content/09-quiz-brief.md`.
 *
 * TODO(Gen 7+): извлечение из markdown в `content-build.ts`.
 *
 * i18n: `briefSchema` — RU (источник правды). EN-вариант — `briefSchemaEn`.
 * `getBriefSchema(locale)` выбирает нужный.
 */

import type { BriefSchema } from '@/content/types';
import type { Locale } from '@/i18n/locales';

export const briefSchema: BriefSchema = {
  intro: 'Давайте знакомиться!',
  motivation: [
    'Пройдите опрос до конца и получите:',
    'Презентацию о платформе Bafsy',
    'Презентацию с кейсами запусков промо-кампаний',
  ],
  steps: [
    {
      id: 'business',
      title: 'Шаг 1 из 4. Расскажите немного о вашей компании',
      question: 'Сфера бизнеса компании',
      hint: 'Мы предложим эффективное решение на основе нашей экспертизы и опыта работы с ведущими брендами.',
      type: 'single',
      options: [
        { id: 'retail', label: 'Розничная торговля' },
        { id: 'wholesale', label: 'Оптовая торговля' },
        { id: 'horeca', label: 'Гостинично-ресторанный бизнес' },
        { id: 'ecommerce', label: 'Электронная коммерция' },
        { id: 'finance', label: 'Финансовые услуги' },
        { id: 'manufacturing', label: 'Производство' },
        { id: 'fmcg', label: 'Товары повседневного спроса' },
        { id: 'banking', label: 'Банки' },
        { id: 'auto', label: 'Авто' },
        { id: 'pharma', label: 'Фармацевтическая промышленность' },
        { id: 'service', label: 'Сервис / Стартап' },
        { id: 'realestate', label: 'Недвижимость' },
        { id: 'other', label: 'Другое' },
      ],
      auxField: {
        id: 'companyUrl',
        label: 'Ссылка на сайт компании',
        placeholder: 'https://example.com',
        inputMode: 'url',
      },
    },
    {
      id: 'task',
      title: 'Шаг 2 из 4. Отлично! Какая у вас задача?',
      question: 'Можно выбрать несколько пунктов',
      hint: 'Мы предложим оптимальный перечень инструментов в зависимости от задач вашей компании.',
      type: 'multi',
      options: [
        { id: 'transformation', label: 'Провести цифровую трансформацию компании' },
        { id: 'promo', label: 'Провести промо-акцию продукта' },
        { id: 'motivation', label: 'Внедрить стимулирующую платформу сотрудников' },
        { id: 'gamification', label: 'Внедрить геймификацию' },
        { id: 'awareness', label: 'Повысить узнаваемость компании / бренда' },
        { id: 'cdp', label: 'Собрать полные данные о клиентах / CDP' },
      ],
      allowCustomInput: true,
    },
    {
      id: 'projectType',
      title: 'Шаг 3 из 4. Выберите подходящее готовое решение',
      question: 'Тип проекта',
      hint: 'Один из типов проектов, которые уже успешно используют мировые бренды на платформе Bafsy.',
      type: 'single',
      skippable: true,
      options: [
        { id: 'landing', label: 'Лендинг' },
        { id: 'promo-codes', label: 'Промо-акция с кодовой механикой' },
        { id: 'promo-receipts', label: 'Промо-акция с чековой механикой' },
        { id: 'promo-contest', label: 'Промо-акция с конкурсной механикой' },
        { id: 'motivation-platform', label: 'Платформа стимулирования сотрудников' },
        { id: 'aggregated-promo', label: 'Агрегированное промо' },
        { id: 'btl', label: 'BTL (приложение промоутера)' },
        { id: 'conlan', label: 'ConLan (Чат-бот)' },
        { id: 'corporate', label: 'Корпоративные сервисы' },
        { id: 'quiz', label: 'Квиз / Викторина' },
        { id: 'games', label: 'Игры' },
        { id: 'interactive', label: 'Интерактивный контент' },
        { id: 'brandbook', label: 'Цифровой брендбук' },
        { id: 'qr', label: 'Генератор QR-кодов' },
      ],
    },
    {
      id: 'integration',
      title: 'Шаг 4 из 4. Нужна интеграция с вашей системой (CRM и т. д.)?',
      hint: 'Bafsy хорошо интегрируется в любую среду — в облаке или on-premise.',
      type: 'single',
      skippable: true,
      options: [
        { id: 'yes', label: 'Да, понадобится интеграция с системой' },
        { id: 'no', label: 'Нет, не понадобится' },
      ],
    },
  ],
  final: {
    title: 'Спасибо! Оставьте контакты и получите:',
    bullets: ['Презентацию о платформе Bafsy', 'Презентацию с кейсами запусков промо-кампаний'],
    ctaLabel: 'Получить результаты',
    consent: 'Я принимаю условия политики конфиденциальности.',
  },
};

export const briefSchemaEn: BriefSchema = {
  intro: "Let's get acquainted!",
  motivation: [
    'Complete the survey and get:',
    'A Bafsy platform deck',
    'A deck of promo-campaign case studies',
  ],
  steps: [
    {
      id: 'business',
      title: 'Step 1 of 4. Tell us a bit about your company',
      question: 'Your industry',
      hint: 'We will propose the right approach based on our expertise and work with leading brands.',
      type: 'single',
      options: [
        { id: 'retail', label: 'Retail' },
        { id: 'wholesale', label: 'Wholesale' },
        { id: 'horeca', label: 'HoReCa (hotels, restaurants, catering)' },
        { id: 'ecommerce', label: 'E-commerce' },
        { id: 'finance', label: 'Financial services' },
        { id: 'manufacturing', label: 'Manufacturing' },
        { id: 'fmcg', label: 'FMCG' },
        { id: 'banking', label: 'Banking' },
        { id: 'auto', label: 'Automotive' },
        { id: 'pharma', label: 'Pharma' },
        { id: 'service', label: 'Services / startup' },
        { id: 'realestate', label: 'Real estate' },
        { id: 'other', label: 'Other' },
      ],
      auxField: {
        id: 'companyUrl',
        label: 'Company website',
        placeholder: 'https://example.com',
        inputMode: 'url',
      },
    },
    {
      id: 'task',
      title: 'Step 2 of 4. Great! What is the task?',
      question: 'You can pick more than one',
      hint: 'We will suggest the right toolkit based on what you need to get done.',
      type: 'multi',
      options: [
        { id: 'transformation', label: 'Run a digital transformation' },
        { id: 'promo', label: 'Run a product promo' },
        { id: 'motivation', label: 'Roll out an employee incentive platform' },
        { id: 'gamification', label: 'Add gamification' },
        { id: 'awareness', label: 'Lift brand awareness' },
        { id: 'cdp', label: 'Gather full customer data / CDP' },
      ],
      allowCustomInput: true,
    },
    {
      id: 'projectType',
      title: 'Step 3 of 4. Pick the ready-made solution that fits',
      question: 'Project type',
      hint: 'One of the project types global brands already run on Bafsy.',
      type: 'single',
      skippable: true,
      options: [
        { id: 'landing', label: 'Landing page' },
        { id: 'promo-codes', label: 'Code-based promo' },
        { id: 'promo-receipts', label: 'Receipt-based promo' },
        { id: 'promo-contest', label: 'Contest-based promo' },
        { id: 'motivation-platform', label: 'Employee incentive platform' },
        { id: 'aggregated-promo', label: 'Aggregated promo' },
        { id: 'btl', label: 'BTL (promoter app)' },
        { id: 'conlan', label: 'ConLan (chatbot)' },
        { id: 'corporate', label: 'Corporate services' },
        { id: 'quiz', label: 'Quiz / trivia' },
        { id: 'games', label: 'Games' },
        { id: 'interactive', label: 'Interactive content' },
        { id: 'brandbook', label: 'Digital brandbook' },
        { id: 'qr', label: 'QR code generator' },
      ],
    },
    {
      id: 'integration',
      title: 'Step 4 of 4. Do you need integration with your systems (CRM, etc.)?',
      hint: 'Bafsy slots into any environment — cloud or on-premise.',
      type: 'single',
      skippable: true,
      options: [
        { id: 'yes', label: 'Yes, we need an integration' },
        { id: 'no', label: 'No, not needed' },
      ],
    },
  ],
  final: {
    title: 'Thanks! Share your details and get:',
    bullets: ['A Bafsy platform deck', 'A deck of promo-campaign case studies'],
    ctaLabel: 'Get the results',
    consent: 'I accept the privacy policy.',
  },
};

export function getBriefSchema(locale: Locale): BriefSchema {
  return locale === 'en' ? briefSchemaEn : briefSchema;
}
